import { Capacitor, registerPlugin } from "@capacitor/core";
import type { PostPaymentTerminalTapToPaySession } from "@/server-types";
import { sentryLog } from "@/utils/sentry-utils";

export type TapToPaySession =
  PostPaymentTerminalTapToPaySession["response"]["session"];

export type TapToPayUnavailableReason =
  | "unsupported_platform"
  | "app_update_required"
  | "sdk_missing"
  | "no_nfc"
  | "nfc_disabled"
  | "location_disabled"
  | "unsupported_device"
  | "os_update_required"
  | "requirements_not_met"
  | "not_available_for_merchant";

/** Plugin reasons: everything except the two the web layer decides itself. */
type NativeUnavailableReason = Exclude<
  TapToPayUnavailableReason,
  "unsupported_platform" | "app_update_required"
>;

export type TapToPayAvailability =
  | { available: true }
  | { available: false; reason: TapToPayUnavailableReason };

export type TapToPayProgressStep =
  | "card_requested"
  | "card_presented"
  | "cvm_requested"
  | "cvm_presented";

/** What SumUp's SDK said happened. The server only treats it as a hint. */
export type TapToPayOutcome = {
  outcome: "done" | "failed" | "canceled" | "unknown";
  transactionCode?: string;
  cardScheme?: string;
  lastFour?: string;
  errorName?: string;
  errorBase?: string;
  errorMessage?: string;
};

export type TapToPayDevice = { deviceId: string; model: string };

/** Whether this phone and merchant have accepted Apple's Tap to Pay terms. Read live, never stored. */
export type TapToPayActivation =
  | { status: "activated" }
  | { status: "not_activated" }
  | { status: "unavailable"; reason: TapToPayUnavailableReason };

type SumUpTapToPayPlugin = {
  getAvailability(): Promise<
    { available: true } | { available: false; reason: NativeUnavailableReason }
  >;
  getDevice(): Promise<TapToPayDevice>;
  setAccessToken(options: { accessToken: string }): Promise<void>;
  initialize(options: {
    merchantCode: string;
    affiliateKey: string;
  }): Promise<void>;
  /** iOS only: activation is Apple's per-device, per-merchant terms acceptance. */
  checkActivation(): Promise<{
    available: boolean;
    activated: boolean;
    reason?: NativeUnavailableReason;
  }>;
  presentActivation(): Promise<{ activated: boolean }>;
  showEducation(): Promise<void>;
  getProductName(): Promise<{ name: string }>;
  startPayment(options: {
    amountCents: number;
    clientTransactionId: string;
    affiliateKey: string;
    cardWaitSeconds: number;
  }): Promise<TapToPayOutcome>;
  tearDown(): Promise<void>;
  addListener(
    eventName: "paymentProgress",
    listener: (event: { step: TapToPayProgressStep }) => void,
  ): Promise<{ remove: () => Promise<void> }>;
};

const PLUGIN_NAME = "SumUpTapToPay";
const SumUpTapToPay = registerPlugin<SumUpTapToPayPlugin>(PLUGIN_NAME);

const NATIVE_PLATFORMS = ["android", "ios"];

function isNativeTapToPayPlatform(): boolean {
  return NATIVE_PLATFORMS.includes(Capacitor.getPlatform());
}

/** iPhones must accept Apple's terms (SumUp's "activation") before the first payment. */
export function tapToPayNeedsActivation(): boolean {
  return Capacitor.getPlatform() === "ios";
}

/**
 * Whether this device can take a Tap to Pay payment. The web app also runs
 * in store builds that predate the plugin.
 */
export async function getTapToPayAvailability(): Promise<TapToPayAvailability> {
  if (!isNativeTapToPayPlatform()) {
    return { available: false, reason: "unsupported_platform" };
  }
  if (!Capacitor.isPluginAvailable(PLUGIN_NAME)) {
    return { available: false, reason: "app_update_required" };
  }

  try {
    return await SumUpTapToPay.getAvailability();
  } catch (error) {
    sentryLog.warn("Tap to Pay availability probe failed", {
      cause: error instanceof Error ? error.message : String(error),
    });
    return { available: false, reason: "app_update_required" };
  }
}

/** The SDK never answered; its callback was lost or it is still waiting on something we cannot see. */
export class TapToPayTimeoutError extends Error {
  readonly data = { errorName: "TapToPayTimeout" };

  constructor(step: string) {
    super(`Tap to Pay ${step} did not answer`);
    this.name = "TapToPayTimeoutError";
  }
}

// Logging in can wait on the iOS location prompt, so leave time to read it.
const LOGIN_TIMEOUT_MS = 90_000;
// Activation and education wait on the merchant reading Apple's screens.
const INTERACTIVE_TIMEOUT_MS = 5 * 60_000;
// Apple's card sheet times out by itself; past this the SDK's answer was lost.
const PAYMENT_RESULT_GRACE_MS = 60_000;
const TEAR_DOWN_TIMEOUT_MS = 15_000;

let sdkQueue: Promise<unknown> = Promise.resolve();

/**
 * Runs SDK work one call at a time. SumUp's SDK holds a single session, so
 * overlapping logins overwrite each other's token or location prompt.
 */
function exclusively<T>(task: () => Promise<T>): Promise<T> {
  const run = sdkQueue.then(task, task);
  sdkQueue = run.catch(() => undefined);
  return run;
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  step: string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new TapToPayTimeoutError(step)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export function getTapToPayDevice(): Promise<TapToPayDevice> {
  return SumUpTapToPay.getDevice();
}

async function logIn(session: TapToPaySession): Promise<void> {
  await SumUpTapToPay.setAccessToken({ accessToken: session.accessToken });
  await SumUpTapToPay.initialize({
    merchantCode: session.merchantCode,
    affiliateKey: session.affiliateKey,
  });
}

/**
 * Logs the SDK in as the session's merchant and asks SumUp whether this
 * iPhone is activated. Android needs no activation. Throws when the SDK
 * cannot log in, like `runTapToPayPayment`.
 */
export async function checkTapToPayActivation(
  session: TapToPaySession,
): Promise<TapToPayActivation> {
  if (!tapToPayNeedsActivation()) return { status: "activated" };
  const result = await exclusively(() =>
    withTimeout(
      logIn(session).then(() => SumUpTapToPay.checkActivation()),
      LOGIN_TIMEOUT_MS,
      "login",
    ),
  );
  if (!result.available) {
    return {
      status: "unavailable",
      reason: result.reason ?? "not_available_for_merchant",
    };
  }
  return { status: result.activated ? "activated" : "not_activated" };
}

export type TapToPayActivationResult = {
  activated: boolean;
  errorName?: string;
  errorMessage?: string;
};

/**
 * Opens SumUp's activation: Apple's terms, the device setup progress and the
 * merchant education. Needs a logged-in SDK; resolves whether it ended activated.
 */
export async function activateTapToPay(): Promise<TapToPayActivationResult> {
  try {
    const { activated } = await exclusively(() =>
      withTimeout(
        SumUpTapToPay.presentActivation(),
        INTERACTIVE_TIMEOUT_MS,
        "activation",
      ),
    );
    return { activated };
  } catch (error) {
    const data = (error as { data?: { errorName?: string } }).data;
    sentryLog.warn("Tap to Pay activation failed", {
      errorName: data?.errorName,
      cause: error instanceof Error ? error.message : String(error),
    });
    return {
      activated: false,
      errorName: data?.errorName,
      errorMessage: error instanceof Error ? error.message : undefined,
    };
  }
}

/** Apple's merchant education on how to take payments; resolves false when it could not open. */
export async function showTapToPayEducation(): Promise<boolean> {
  try {
    await exclusively(() =>
      withTimeout(
        SumUpTapToPay.showEducation(),
        INTERACTIVE_TIMEOUT_MS,
        "education",
      ),
    );
    return true;
  } catch (error) {
    sentryLog.warn("Tap to Pay education could not open", {
      cause: error instanceof Error ? error.message : String(error),
    });
    return false;
  }
}

/** Apple's localized name for the feature on iPhone; null elsewhere, where our own copy applies. */
export async function getTapToPayProductName(): Promise<string | null> {
  if (!tapToPayNeedsActivation() || !Capacitor.isPluginAvailable(PLUGIN_NAME)) {
    return null;
  }
  try {
    const { name } = await SumUpTapToPay.getProductName();
    return name || null;
  } catch (error) {
    sentryLog.warn("Tap to Pay product name unavailable", {
      cause: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * Charges one payment on this phone. The session must be fresh: SumUp's SDK
 * authenticates with the merchant's short-lived access token.
 */
export async function runTapToPayPayment(input: {
  session: TapToPaySession;
  terminalPaymentId: string;
  amountCents: number;
  onProgress?: (step: TapToPayProgressStep) => void;
}): Promise<TapToPayOutcome> {
  return exclusively(async () => {
    await withTimeout(logIn(input.session), LOGIN_TIMEOUT_MS, "login");

    const listener = input.onProgress
      ? await SumUpTapToPay.addListener("paymentProgress", (event) =>
          input.onProgress?.(event.step),
        )
      : null;
    try {
      return await withTimeout(
        SumUpTapToPay.startPayment({
          amountCents: input.amountCents,
          clientTransactionId: input.terminalPaymentId,
          affiliateKey: input.session.affiliateKey,
          cardWaitSeconds: input.session.cardWaitSeconds,
        }),
        input.session.cardWaitSeconds * 1000 + PAYMENT_RESULT_GRACE_MS,
        "payment",
      );
    } catch (error) {
      if (!(error instanceof TapToPayTimeoutError)) throw error;
      // The card may still have been charged, so let SumUp's record decide.
      sentryLog.warn("Tap to Pay payment never reported a result", {
        terminalPaymentId: input.terminalPaymentId,
      });
      return {
        outcome: "unknown",
        errorName: error.data.errorName,
        errorMessage: error.message,
      };
    } finally {
      await listener?.remove();
    }
  });
}

/** Ends the SDK session so the next user on this phone cannot charge as this merchant. */
export async function tearDownTapToPay(): Promise<void> {
  if (
    !isNativeTapToPayPlatform() ||
    !Capacitor.isPluginAvailable(PLUGIN_NAME)
  ) {
    return;
  }
  try {
    await exclusively(() =>
      withTimeout(SumUpTapToPay.tearDown(), TEAR_DOWN_TIMEOUT_MS, "tear down"),
    );
  } catch (error) {
    sentryLog.warn("Tap to Pay tear down failed", {
      cause: error instanceof Error ? error.message : String(error),
    });
  }
}

/** The reason kept on the payment: the SDK's own names, for support to look up. */
export function tapToPayFailureReason(outcome: TapToPayOutcome): string | null {
  if (!outcome.errorName && !outcome.errorMessage) return null;
  return [outcome.errorName, outcome.errorMessage].filter(Boolean).join(": ");
}
